import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Markdown, useTheme } from '@Sage/hoi-poi-ui';

import Artifacts from 'shards/Copilot/CopilotChat/components/Artifacts';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBarFeedback');

const CONTENT_FADE_MS = 200;
const COLLAPSE_MS = 300;

const CopilotBarFeedback = ({ feedback, artifacts, onDismiss }) => {
    const classes = useStyles();
    const theme = useTheme();
    const autoHideTimerRef = useRef(null);
    const transitionTimerRef = useRef(null);
    const rafRef = useRef(null);

    const { autoHide = false, dismissible = false, isStreaming = false, message } = feedback || {};

    // Track displayed message and animation states separately so
    // height (grid-template-rows) and content opacity can transition independently.
    const [displayedMessage, setDisplayedMessage] = useState(message);
    const [isExpanded, setIsExpanded] = useState(!!message);
    const [isContentVisible, setIsContentVisible] = useState(!!message);

    // Ref mirrors `displayedMessage` state so the transition effect can read
    // the current value without adding it to its dependency array (avoids a
    // spurious re-run every time displayedMessage is updated by the effect itself).
    const displayedMessageRef = useRef(displayedMessage);
    const prevMessageRef = useRef(message);
    const messageContainerRef = useRef(null);

    const clearPendingTimers = useCallback(() => {
        if (transitionTimerRef.current) {
            clearTimeout(transitionTimerRef.current);
            transitionTimerRef.current = null;
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (message === prevMessageRef.current) return;
        prevMessageRef.current = message;

        clearPendingTimers();

        if (displayedMessageRef.current && message) {
            // Streaming update: parent signals content is still incoming — skip crossfade
            if (isStreaming) {
                displayedMessageRef.current = message;
                setDisplayedMessage(message);
                return;
            }
            // Message replaced — crossfade content only (height stays)
            setIsContentVisible(false);
            transitionTimerRef.current = setTimeout(() => {
                displayedMessageRef.current = message;
                setDisplayedMessage(message);
                setIsContentVisible(true);
                transitionTimerRef.current = null;
            }, CONTENT_FADE_MS);
        } else if (message) {
            // New message appearing — expand height and fade in
            displayedMessageRef.current = message;
            setDisplayedMessage(message);
            rafRef.current = requestAnimationFrame(() => {
                setIsExpanded(true);
                setIsContentVisible(true);
                rafRef.current = null;
            });
        } else {
            // Message cleared — fade out content and collapse height
            setIsContentVisible(false);
            setIsExpanded(false);
            transitionTimerRef.current = setTimeout(() => {
                displayedMessageRef.current = null;
                setDisplayedMessage(null);
                transitionTimerRef.current = null;
            }, COLLAPSE_MS);
        }

        return clearPendingTimers;
    }, [message, isStreaming, clearPendingTimers]);

    useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        }
    }, [displayedMessage]);

    useEffect(() => {
        if (autoHide && autoHide > 0) {
            autoHideTimerRef.current = setTimeout(() => {
                onDismiss?.();
            }, autoHide);
        }

        return () => {
            if (autoHideTimerRef.current) {
                clearTimeout(autoHideTimerRef.current);
                autoHideTimerRef.current = null;
            }
        };
    }, [autoHide, message, onDismiss]);

    const handleDismiss = useCallback(() => {
        if (autoHideTimerRef.current) {
            clearTimeout(autoHideTimerRef.current);
        }
        onDismiss?.();
    }, [onDismiss]);

    const iconColor = theme.colors.primary.black;

    const hasArtifacts = artifacts?.length > 0;

    if (!displayedMessage && !message && !hasArtifacts) return null;

    return (
        <div
            className={classNames(classes.wrapper, {
                [classes.wrapperExpanded]: isExpanded || hasArtifacts,
                [classes.wrapperDismissible]: dismissible && isContentVisible,
            })}
        >
            <div className={classes.wrapperInner}>
                <div
                    className={classNames(classes.content, {
                        [classes.contentVisible]: isContentVisible || hasArtifacts,
                    })}
                >
                    <div className={classes.root}>
                        <div className={classes.message} ref={messageContainerRef}>
                            {(displayedMessage || message) && (
                                <Markdown content={displayedMessage || message} showTableActions />
                            )}
                            {hasArtifacts && (
                                <Artifacts
                                    artifacts={artifacts}
                                    finished
                                    className={classes.artifacts}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {dismissible && (
                <div className={classes.dismissButton} onClick={handleDismiss}>
                    <Icon color={iconColor} name="close" />
                </div>
            )}
        </div>
    );
};

CopilotBarFeedback.propTypes = {
    feedback: PropTypes.shape({
        autoHide: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        dismissible: PropTypes.bool,
        isStreaming: PropTypes.bool,
        message: PropTypes.string,
    }),
    artifacts: PropTypes.array,
    onDismiss: PropTypes.func,
};

const MemoizedCopilotBarFeedback = memo(CopilotBarFeedback);
MemoizedCopilotBarFeedback.displayName = 'CopilotBarFeedback';

export default MemoizedCopilotBarFeedback;
